import type * as React from "react";
import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import type { SupportedLocale } from "@app/i18n";
import { GoogleIcon } from "../../components/Icons";

/** 서버 socialProviders 키와 1:1. 프로바이더를 추가하려면 이 유니온과 아래 레지스트리에
 * 항목을 더하고, 서버(shared/lib/auth.ts)에도 자격증명·게이팅을 붙이면 정렬/버튼/더보기
 * UI 가 자동으로 처리한다. */
export type SocialProviderId = "google";

export interface SocialProvider {
  id: SocialProviderId;
  /** 버튼 라벨 디스크립터. "{Provider}로 계속하기" — 프로바이더 고유명은 번역 대상이 아니다.
   * 렌더 시점에 i18n._() 로 해석해야 언어 전환에 반응한다(모듈 로드 시 문자열로 굳히면 안 됨). */
  brand: MessageDescriptor;
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  /** 이 언어에서 "우선(primary)"으로 노출할 언어 목록. 비면 항상 "더 보기" 안으로 접힌다. */
  primaryFor: readonly SupportedLocale[];
}

// 지금은 Google 만 서버 연동돼 있다. 나머지(Apple/Kakao/Naver/GitHub …)는 서버
// socialProviders 가 붙는 순서대로 여기에 추가한다 — primaryFor 로 언어별 우선순위를
// 준다(예: kakao → ["ko"], github → ["en"]).
export const SOCIAL_PROVIDERS: Record<SocialProviderId, SocialProvider> = {
  google: {
    id: "google",
    brand: msg`Google로 계속하기`,
    Icon: GoogleIcon,
    // Google 은 국내외 공통 1차 옵션 — 모든 언어에서 우선 노출.
    primaryFor: ["ko", "en"],
  },
};

/** 서버가 활성화한 id 목록(/api/auth-providers)을 레지스트리 메타로 해석한다.
 * 등록 순서를 보존하고, 레지스트리에 없는 미지의 id 는 조용히 버린다(버전 스큐 방어). */
export function resolveSocialProviders(ids: readonly string[]): SocialProvider[] {
  return ids
    .map((id) => SOCIAL_PROVIDERS[id as SocialProviderId])
    .filter((provider): provider is SocialProvider => Boolean(provider));
}
